import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { X, CheckCircle, AlertCircle, Info } from "lucide-react";

/**
 * Toast notification component
 * @param {Object} props
 * @param {string} props.message - The message to display
 * @param {function} props.onClose - Function to call when the toast is closed
 * @param {string} props.type - Type of toast: 'success', 'warning', 'error', 'info'
 * @param {number} props.duration - Duration in ms before auto-closing (0 for no auto-close)
 * @param {string} props.position - Position of the toast
 */
const Toast = ({
  message,
  onClose,
  type = "success",
  duration = 5000,
  position = "top-right", // Still receive position for potential styling
}) => {
  const [isExiting, setIsExiting] = useState(false);

  // Handle auto-close
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration]);

  // Animation for smooth exit
  const handleClose = () => {
    setIsExiting(true);
    // Wait for animation to complete
    setTimeout(() => {
      if (onClose) onClose();
    }, 300);
  };

  // Get the icon based on type
  const getIcon = () => {
    switch (type) {
      case "success":
        return <CheckCircle className="text-green-500" size={20} />;
      case "warning":
        return <AlertCircle className="text-yellow-500" size={20} />;
      case "error":
        return <AlertCircle className="text-red-500" size={20} />;
      case "info":
      default:
        return <Info className="text-blue-500" size={20} />;
    }
  };

  // Get background color based on type
  const getBgColor = () => {
    switch (type) {
      case "success":
        return "bg-green-50 border-green-200";
      case "warning":
        return "bg-yellow-50 border-yellow-200";
      case "error":
        return "bg-red-50 border-red-200";
      case "info":
      default:
        return "bg-blue-50 border-blue-200";
    }
  };

  // Get text color based on type
  const getTextColor = () => {
    switch (type) {
      case "success":
        return "text-green-800";
      case "warning":
        return "text-yellow-800";
      case "error":
        return "text-red-800";
      case "info":
      default:
        return "text-blue-800";
    }
  };

  return (
    <div
      className={`transition-all duration-300 ease-in-out ${
        isExiting ? "opacity-0 transform translate-y-2" : "opacity-100"
      }`}
    >
      <div
        className={`flex items-center px-4 py-3 rounded-lg shadow-lg border ${getBgColor()} ${getTextColor()} max-w-xs`}
        role="alert"
      >
        <div className="flex-shrink-0 mr-3">{getIcon()}</div>
        <div className="text-sm font-medium">{message}</div>
        <button
          onClick={handleClose}
          className="ml-auto -mr-1 text-gray-400 hover:text-gray-500 focus:outline-none"
          aria-label="Close"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

Toast.propTypes = {
  message: PropTypes.string.isRequired,
  onClose: PropTypes.func,
  type: PropTypes.oneOf(["success", "warning", "error", "info"]),
  duration: PropTypes.number,
  position: PropTypes.oneOf([
    "top-right",
    "top-left",
    "bottom-right",
    "bottom-left",
    "top-center",
    "bottom-center",
  ]),
};

export default Toast;
